import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import ResearchLayout from '../../components/ResearchLayout';
import api from '../../services/api';

const EditReportPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [report, setReport] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    filters: {
      dateRange: '30days',
      minScore: '',
      maxScore: '',
      ageGroup: '',
      gender: ''
    }
  });
  const [error, setError] = useState(null);

  useEffect(() => {
    loadReport();
  }, [id]);

  const loadReport = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/reports/${id}`);
      if (response.data.success) {
        const reportData = response.data.report;
        setReport(reportData);
        
        // Initialize form data from report
        setFormData({
          title: reportData.title || '',
          description: reportData.description || '',
          filters: reportData.filters || {
            dateRange: '30days',
            minScore: '',
            maxScore: '',
            ageGroup: '',
            gender: ''
          }
        });
      } else {
        setError('Failed to load report data');
      }
    } catch (err) {
      console.error("Failed to load report", err);
      setError("Failed to load report. The report may have been deleted or you don't have permission to edit it.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        [name]: value
      }
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      setError('Report title is required');
      return;
    }
    
    setSaving(true);
    try {
      const response = await api.put(`/reports/${id}`, {
        title: formData.title,
        description: formData.description,
        filters: formData.filters
      });
      
      if (response.data.success) {
        navigate(`/statistician/reports/${id}`, { replace: true });
      } else {
        setError('Failed to update report');
      }
    } catch (err) {
      console.error("Failed to update report", err);
      setError("Failed to update report. Please try again later.");
    } finally {
      setSaving(false);
    }
  };

  const getReportTypeLabel = (type) => {
    switch(type) {
      case 'statistical': return 'Statistical Analysis';
      case 'summary': return 'Summary Report';
      case 'analysis': return 'Detailed Analysis';
      case 'dashboard': return 'Dashboard Report';
      default: return 'Report';
    }
  };

  if (loading) {
    return (
      <ResearchLayout pageTitle="Edit Report">
        <div className="d-flex justify-content-center align-items-center" style={{ height: '60vh' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </ResearchLayout>
    );
  }

  if (error && !report) {
    return (
      <ResearchLayout pageTitle="Edit Report">
        <div className="container-fluid">
          <div className="alert alert-danger">
            <i className="fas fa-exclamation-circle me-2"></i>
            {error}
          </div>
          <div className="text-center mt-4">
            <Link to="/statistician/reports/view" className="btn btn-outline-primary">
              <i className="fas fa-arrow-left me-2"></i>
              Back to Reports
            </Link>
          </div>
        </div>
      </ResearchLayout>
    );
  }

  return (
    <ResearchLayout pageTitle="Edit Report">
      <div className="container-fluid">
        <div className="row mb-4">
          <div className="col">
            <div className="card border-0 shadow-sm bg-gradient-info text-white">
              <div className="card-body" style={{backgroundColor: '#7B68EE'}}>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h3 className="mb-2">Edit Report</h3>
                    <p className="mb-0 opacity-75">
                      Update your {getReportTypeLabel(report?.report_type).toLowerCase()}
                    </p>
                  </div>
                  <div>
                    <Link to={`/statistician/reports/${id}`} className="btn btn-light me-2">
                      <i className="fas fa-eye me-2"></i>
                      View Report
                    </Link>
                    <Link to="/statistician/reports/view" className="btn btn-outline-light">
                      <i className="fas fa-list me-2"></i>
                      All Reports
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger mb-4">
            <i className="fas fa-exclamation-circle me-2"></i>
            {error}
          </div>
        )}

        <div className="row">
          <div className="col-lg-8">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white">
                <h5 className="mb-0">Report Information</h5>
              </div>
              <div className="card-body">
                <form onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <label className="form-label">Report Type</label>
                    <input 
                      type="text" 
                      className="form-control" 
                      value={getReportTypeLabel(report?.report_type)}
                      disabled
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label">Report Title</label>
                    <input
                      type="text"
                      className="form-control"
                      name="title"
                      value={formData.title}
                      onChange={handleInputChange}
                      placeholder="Enter report title"
                      required
                    />
                  </div>
                  
                  <div className="mb-3">
                    <label className="form-label">Description</label>
                    <textarea
                      className="form-control"
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows="3"
                      placeholder="Report description (optional)"
                    ></textarea>
                  </div>
                  
                  <hr className="my-4" />
                  
                  <h6 className="mb-3">Filters</h6>
                  
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Date Range</label>
                      <select 
                        className="form-select" 
                        name="dateRange" 
                        value={formData.filters?.dateRange || '30days'}
                        onChange={handleFilterChange}
                      >
                        <option value="7days">Last 7 days</option>
                        <option value="30days">Last 30 days</option>
                        <option value="90days">Last 90 days</option>
                        <option value="1year">Last year</option>
                        <option value="all">All time</option>
                      </select>
                    </div>
                    
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Age Group</label>
                      <select 
                        className="form-select" 
                        name="ageGroup" 
                        value={formData.filters?.ageGroup || ''}
                        onChange={handleFilterChange}
                      >
                        <option value="">All ages</option>
                        <option value="18-30">18-30</option>
                        <option value="31-45">31-45</option>
                        <option value="46-60">46-60</option>
                        <option value="60+">60+</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="row">
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Min Health Score</label>
                      <input
                        type="number"
                        className="form-control"
                        name="minScore"
                        min="0"
                        max="100"
                        value={formData.filters?.minScore || ''}
                        onChange={handleFilterChange}
                        placeholder="Minimum score"
                      />
                    </div>
                    
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Max Health Score</label>
                      <input
                        type="number"
                        className="form-control"
                        name="maxScore"
                        min="0"
                        max="100"
                        value={formData.filters?.maxScore || ''}
                        onChange={handleFilterChange}
                        placeholder="Maximum score"
                      />
                    </div>
                    
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Gender</label>
                      <select 
                        className="form-select" 
                        name="gender" 
                        value={formData.filters?.gender || ''}
                        onChange={handleFilterChange}
                      >
                        <option value="">All genders</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="d-flex justify-content-between mt-4">
                    <Link to={`/statistician/reports/${id}`} className="btn btn-outline-secondary">
                      Cancel
                    </Link>
                    <button 
                      type="submit" 
                      className="btn btn-primary" 
                      disabled={saving}
                    >
                      {saving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save me-2"></i>
                          Save Changes
                        </>
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          
          <div className="col-lg-4">
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-header bg-white">
                <h5 className="mb-0">Report Details</h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <span className="d-block text-muted small">Report ID</span>
                  <span>{report?.id}</span>
                </div>
                <div className="mb-3">
                  <span className="d-block text-muted small">Created</span>
                  <span>
                    {report?.created_at ? new Date(report.created_at).toLocaleDateString('en-US', {
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    }) : 'Unknown'}
                  </span>
                </div>
                {report?.updated_at && report.updated_at !== report.created_at && (
                  <div className="mb-3">
                    <span className="d-block text-muted small">Last Updated</span>
                    <span>
                      {new Date(report.updated_at).toLocaleDateString('en-US', {
                        year: 'numeric', 
                        month: 'long', 
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </div>
                )}
                <div className="mb-3">
                  <span className="d-block text-muted small">Report Type</span>
                  <span className="badge bg-info">
                    {getReportTypeLabel(report?.report_type)}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white">
                <h5 className="mb-0">Help</h5>
              </div>
              <div className="card-body">
                <div className="alert alert-info mb-0">
                  <h6><i className="fas fa-info-circle me-2"></i>About Editing Reports</h6>
                  <p className="small mb-0">
                    You can update the report title, description, and filters. The report data will not be regenerated when you save your changes. To generate updated data, create a new report with your desired filters.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ResearchLayout>
  );
};

export default EditReportPage;